import { useGetUsersByIds } from '../../../../../domain/Users';
import { RemoteConfigUtils } from '../../../../../utils/RemoteConfigUtils';
import { CustomSelect } from '../../../UsersList/components/CustomSelect';

/**
 * RecruitersCombobox component for selecting recruiters
 * Automatically fetches recruiters from remote config and displays them in a dropdown
 *
 * @param {Object} props - Component props
 * @param {Function} props.onChange - Callback function that receives an object { value: string, label: string }
 *   - value: Empty string for "All" option, or recruiter ID for specific recruiter
 *   - label: Display text (e.g., "All", "John Doe", etc.)
 * @param {Object} [props.lang] - Language/text configuration
 * @param {string} [props.lang.allOptionsText='All'] - Text for the "All" option (defaults to "All")
 * @param {string} [props.value=''] - Currently selected recruiter ID (empty string for "All")
 * @param {string} [props.label] - Label text displayed above the select
 * @param {Function} [props.keyExtractor] - Optional function to extract the key from recruiter object. Receives recruiter object and should return the key to use. Falls back to recruiter.id if not provided or returns falsy value.
 *
 * @example
 * <RecruitersCombobox
 *   onChange={({ value, label }) => {
 *     console.log('Selected:', value, label);
 *     // value: '' for All, or recruiter ID
 *     // label: 'All' or recruiter name
 *   }}
 *   lang={{ allOptionsText: 'All Recruiters' }}
 *   value=""
 *   label="Select Recruiter"
 *   keyExtractor={(recruiter) => recruiter.id}
 * />
 */
export const RecruitersCombobox = ({ onChange, lang = {}, value = '', label, keyExtractor }) => {
  const { data: recruiters } = useGetUsersByIds(RemoteConfigUtils.getPermanentRecruiterIds());

  const { allOptionsText = 'All' } = lang;

  // Prepare options with "All" option as first item
  const options = [
    { code: '', name: allOptionsText },
    ...(recruiters || []).map((recruiter) => ({
      code: keyExtractor(recruiter) || recruiter.id,
      name: recruiter.email,
    })),
  ];

  const handleChange = (_id, selectedValue) => {
    const selected = options.find((option) => option.code === selectedValue);

    if (selected) {
      onChange({ value: selected.code, label: selected.name });
    }
  };

  return (
    <CustomSelect
      id="recruiter"
      label={label}
      options={options}
      value={value}
      onChange={handleChange}
    />
  );
};
